import React from 'react';

// This generates the HTML content for the CTA iframe
export function generateCTAHtml(cta, displayMode = 'centered', isMobile = false) {
  const isVertical = displayMode === 'vertical';
  const isHorizontal = displayMode === 'horizontal';
  const isCentered = displayMode === 'centered';

  const bgStyle = cta.background?.type === 'gradient'
    ? `background: linear-gradient(${cta.background.gradientDirection || '135deg'}, ${cta.background.color}, ${cta.background.gradientColor2});`
    : cta.background?.type === 'image' && cta.background?.imageUrl
    ? `background-image: url(${cta.background.imageUrl}); background-size: cover; background-position: center;`
    : `background-color: ${cta.background?.color || '#6a1fbf'};`;

  const overlayStyle = '';

  const buttonStyle = (btn, dm) => {
    const gradient = btn.useGradient
      ? `background: linear-gradient(135deg, ${btn.bgColor}, ${btn.gradientColor2});`
      : `background-color: ${btn.bgColor};`;
    let size;
    if (dm === 'horizontal') {
      size = btn.size === 'sm' ? 'padding: 6px 12px; font-size: 11px;'
        : btn.size === 'lg' ? 'padding: 12px 24px; font-size: 14px;'
        : 'padding: 10px 18px; font-size: 12px;';
    } else {
      size = btn.size === 'sm' ? 'padding: 8px 16px; font-size: 12px;'
        : btn.size === 'lg' ? 'padding: 16px 32px; font-size: 16px;'
        : 'padding: 12px 24px; font-size: 14px;';
    }
    return `${gradient} color: ${btn.textColor}; border-radius: ${btn.borderRadius}px; ${size} border: none; cursor: pointer; font-weight: 600; text-decoration: none; display: inline-block; transition: transform 0.2s, opacity 0.2s;`;
  };

  const secondaryButtonStyle = (btn, dm) => {
    let size;
    if (dm === 'horizontal') {
      size = btn.size === 'sm' ? 'padding: 5px 10px; font-size: 10px;'
        : btn.size === 'lg' ? 'padding: 11px 22px; font-size: 13px;'
        : 'padding: 9px 16px; font-size: 11px;';
    } else {
      size = btn.size === 'sm' ? 'padding: 8px 16px; font-size: 12px;'
        : btn.size === 'lg' ? 'padding: 16px 32px; font-size: 16px;'
        : 'padding: 12px 24px; font-size: 14px;';
    }
    return `background: transparent; color: ${btn.textColor}; border: 2px solid ${btn.borderColor || btn.textColor}; border-radius: ${btn.borderRadius}px; ${size} cursor: pointer; font-weight: 500; text-decoration: none; display: inline-block; transition: transform 0.2s, opacity 0.2s;`;
  };

  const textAlign = isHorizontal ? 'left' : cta.headline?.alignment || 'center';

  // Use user-set font sizes directly - no capping
  const headlineSize = cta.headline?.size || 28;
  const taglineSize = cta.tagline?.size || 16;
  const bodySize = cta.bodyText?.size || 14;

  const listItemSize = isVertical ? 10 : isHorizontal ? 11 : cta.bodyText?.size || 14;
  const priceFontSize = isVertical ? 24 : isHorizontal ? 28 : 36;
  const strikethroughSize = isVertical ? 12 : isHorizontal ? 14 : 18;

  // Render list items for offer stack template
  const listItemsHtml = cta.listItems?.length > 0
    ? `<ul style="list-style: none; padding: 0; margin: ${isVertical ? '8px' : '16px'} 0; text-align: left;">
        ${cta.listItems.slice(0, isVertical ? 4 : isHorizontal ? 5 : cta.listItems.length).map(item => `
          <li style="display: flex; align-items: center; gap: ${isVertical ? '4px' : '8px'}; margin: ${isVertical ? '4px' : '8px'} 0; color: ${cta.bodyText?.color || '#ffffffcc'}; font-size: ${listItemSize}px;">
            <span style="color: #00c853; font-size: ${isVertical ? '10px' : '14px'};">✓</span>
            ${item}
          </li>
        `).join('')}
       </ul>`
    : '';

  // Render pricing for offer template
  const pricingHtml = cta.pricing?.enabled
    ? `<div style="margin: ${isVertical ? '8px' : '16px'} 0;">
        ${cta.pricing.originalPrice ? `<span style="text-decoration: line-through; color: #ffffff66; font-size: ${strikethroughSize}px; margin-right: 8px;">${cta.pricing.currency}${cta.pricing.originalPrice}</span>` : ''}
        <span style="font-size: ${priceFontSize}px; font-weight: bold; color: #00c853;">${cta.pricing.currency}${cta.pricing.price}</span>
       </div>`
    : '';

  // Render event details
  const eventDetailsFontSize = isVertical ? 10 : isHorizontal ? 12 : 14;
  const eventHtml = cta.eventDetails?.enabled
    ? `<div style="margin: ${isVertical ? '8px' : '16px'} 0; padding: ${isVertical ? '8px 10px' : '12px 16px'}; background: rgba(255,255,255,0.1); border-radius: 8px; display: inline-block;">
        <div style="font-size: ${eventDetailsFontSize}px; color: #ffffff99;">📅 ${cta.eventDetails.date}</div>
        <div style="font-size: ${eventDetailsFontSize}px; color: #ffffff99;">🕐 ${cta.eventDetails.time}</div>
       </div>`
    : '';

  // Image HTML - responsive sizing
  const isImageSide = cta.image?.position === 'left' || cta.image?.position === 'right';
  const customImageSize = cta.image?.customSize ? `${cta.image.customSize}px` : null;
  const imageMaxHeight = customImageSize
    ? customImageSize
    : isVertical
    ? (cta.image?.size === 'small' ? '120px' : cta.image?.size === 'large' ? '300px' : '200px')
    : isHorizontal
    ? (cta.image?.size === 'small' ? '50px' : cta.image?.size === 'large' ? '100px' : '70px')
    : (cta.image?.size === 'small' ? '100px' : cta.image?.size === 'large' ? '250px' : '180px');

  const imageHtml = cta.image?.url
    ? `<img src="${cta.image.url}" alt="" style="
        max-width: ${isImageSide ? '40%' : '100%'};
        max-height: ${isHorizontal || isCentered ? 'min(' + imageMaxHeight + ', calc(100vh - 20px))' : imageMaxHeight};
        height: auto;
        border-radius: ${cta.image.borderRadius || 8}px;
        ${cta.image.shadow ? 'box-shadow: 0 10px 30px rgba(0,0,0,0.3);' : ''}
        object-fit: contain;
        margin: 0;
      " />`
    : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no">
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@400;700&display=swap" rel="stylesheet">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
          width: 100%;
          height: 100%;
          overflow: hidden;
          overflow-y: auto;
        }
        body {
          font-family: ${cta.headline?.font || 'Inter'}, -apple-system, sans-serif;
          ${bgStyle}
          display: flex;
          ${isVertical ? `
            flex-direction: column;
            justify-content: space-evenly;
            align-items: center;
            height: 100%;
            padding: 15px 10px;
          ` : isHorizontal ? `
            flex-direction: row;
            justify-content: center;
            align-items: center;
            padding: 10px 20px;
            gap: 16px;
          ` : `
            flex-direction: row;
            justify-content: center;
            align-items: center;
            padding: ${cta.padding?.top || 24}px ${cta.padding?.right || 24}px ${cta.padding?.bottom || 24}px ${cta.padding?.left || 24}px;
            gap: 20px;
          `}
          text-align: ${textAlign};
          position: relative;
          min-height: 100%;
        }
        .content-wrapper {
          position: relative;
          z-index: 1;
          display: flex;
          ${isHorizontal ? `
            flex-direction: row;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            gap: 16px;
          ` : isVertical ? `
            flex-direction: column;
            align-items: center;
            justify-content: space-evenly;
            width: 100%;
            height: 100%;
          ` : `
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 20px;
            max-width: ${cta.maxWidth || 800}px;
            width: 100%;
            height: 100%;
          `}
        }
        .text-content {
          ${isVertical ? 'width: 100%;' : 'flex: 1; min-width: 0;'}
        }
        .headline {
          font-size: ${headlineSize}px;
          color: ${cta.headline?.color || '#ffffff'};
          font-weight: ${cta.headline?.weight || 'bold'};
          font-style: ${cta.headline?.italic ? 'italic' : 'normal'};
          font-family: ${cta.headline?.font || 'Inter'}, sans-serif;
          margin-bottom: ${isVertical ? '4px' : isHorizontal ? '4px' : '12px'};
          line-height: 1.2;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .tagline {
          font-size: ${taglineSize}px;
          color: ${cta.tagline?.color || '#ffffffcc'};
          font-weight: ${cta.tagline?.weight || 'normal'};
          font-style: ${cta.tagline?.italic ? 'italic' : 'normal'};
          font-family: ${cta.tagline?.font || 'Inter'}, sans-serif;
          margin-bottom: ${isVertical ? '4px' : isHorizontal ? '4px' : '16px'};
          line-height: 1.4;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .body-text {
          font-size: ${bodySize}px;
          color: ${cta.bodyText?.color || '#ffffff99'};
          font-family: ${cta.bodyText?.font || 'Inter'}, sans-serif;
          margin-bottom: ${isVertical ? '4px' : isHorizontal ? '4px' : '16px'};
          line-height: 1.5;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .buttons-wrapper {
          display: flex;
          gap: ${isVertical ? '6px' : '12px'};
          flex-wrap: wrap;
          justify-content: ${cta.buttons?.[0]?.position === 'left' ? 'flex-start' : cta.buttons?.[0]?.position === 'right' ? 'flex-end' : 'center'};
          ${isHorizontal ? 'flex-shrink: 0;' : ''}
          ${isVertical ? 'width: 100%;' : ''}
        }
        .cta-btn {
          transition: transform 0.2s, opacity 0.2s;
          width: fit-content;
        }
        .cta-btn:hover {
          transform: scale(1.05);
          opacity: 0.9;
        }
        .image-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          ${isVertical ? 'width: 100%;' : 'flex-shrink: 0; height: 100%;'}
        }
        .image-wrapper img {
          display: block;
          max-height: 100%;
          width: auto;
        }

        /* Mobile vertical: applied via isMobile flag (not media query, since iframe is always narrow) */
        ${isMobile && isVertical ? `
          .headline {
            font-size: ${Math.min(headlineSize, 18)}px !important;
            margin-bottom: 2px !important;
          }
          .tagline {
            font-size: ${Math.min(taglineSize, 13)}px !important;
            margin-bottom: 2px !important;
          }
          .body-text {
            font-size: ${Math.min(bodySize, 11)}px !important;
            margin-bottom: 2px !important;
          }
          .buttons-wrapper {
            gap: 4px !important;
          }
          .cta-btn {
            font-size: 10px !important;
            padding: 5px 10px !important;
          }
        ` : ''}

        /* Mobile: stack horizontal/centered into column */
        @media (max-width: 480px) {
          ${!isVertical ? `
            body {
              flex-direction: column !important;
              padding: 10px 12px !important;
              gap: 6px !important;
              text-align: center !important;
              overflow: hidden !important;
            }
            .content-wrapper {
              flex-direction: column !important;
              gap: 6px !important;
              height: 100% !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .image-wrapper {
              width: 100% !important;
              height: auto !important;
              flex-shrink: 0 !important;
            }
            .image-wrapper img {
              max-height: 60px !important;
              max-width: 50% !important;
              margin: 0 auto !important;
            }
            .text-content {
              width: 100% !important;
              flex: none !important;
            }
            .headline {
              font-size: clamp(13px, 4.5vw, ${headlineSize}px) !important;
              margin-bottom: 2px !important;
            }
            .tagline {
              font-size: clamp(10px, 2.8vw, ${taglineSize}px) !important;
              margin-bottom: 2px !important;
            }
            .body-text {
              font-size: clamp(9px, 2.2vw, ${bodySize}px) !important;
              margin-bottom: 4px !important;
            }
            .buttons-wrapper {
              justify-content: center !important;
              flex-shrink: 0 !important;
            }
            .cta-btn {
              font-size: 11px !important;
              padding: 6px 14px !important;
            }
          ` : ''}
        }
      </style>
    </head>
    <body>
      ${overlayStyle}
      <div class="content-wrapper">
        ${isHorizontal ? `
          ${cta.image?.url ? `<div class="image-wrapper">${imageHtml}</div>` : ''}
          <div class="text-content">
            ${cta.headline?.text ? `<h1 class="headline">${cta.headline.text}</h1>` : ''}
            ${cta.tagline?.text ? `<p class="tagline">${cta.tagline.text}</p>` : ''}
            ${cta.bodyText?.text ? `<p class="body-text">${cta.bodyText.text}</p>` : ''}
            ${listItemsHtml}
            ${pricingHtml}
            ${eventHtml}
          </div>
          <div class="buttons-wrapper">
            ${cta.buttons?.[0]?.text ? `
              <a href="${cta.buttons[0].url || '#'}" target="_blank" class="cta-btn" style="${buttonStyle(cta.buttons[0], displayMode)}">
                ${cta.buttons[0].text}
              </a>
            ` : ''}
            ${cta.secondaryButton?.enabled && cta.secondaryButton?.text ? `
              <a href="${cta.secondaryButton.url || '#'}" target="_blank" class="cta-btn" style="${secondaryButtonStyle(cta.secondaryButton, displayMode)}">
                ${cta.secondaryButton.text}
              </a>
            ` : ''}
          </div>
        ` : isVertical ? `
          ${cta.image?.url ? `<div class="image-wrapper">${imageHtml}</div>` : ''}
          <div class="text-content">
            ${cta.headline?.text ? `<h1 class="headline">${cta.headline.text}</h1>` : ''}
            ${cta.tagline?.text ? `<p class="tagline">${cta.tagline.text}</p>` : ''}
            ${cta.bodyText?.text ? `<p class="body-text">${cta.bodyText.text}</p>` : ''}
            ${listItemsHtml}
            ${pricingHtml}
            ${eventHtml}
          </div>
          <div class="buttons-wrapper">
            ${cta.buttons?.[0]?.text ? `
              <a href="${cta.buttons[0].url || '#'}" target="_blank" class="cta-btn" style="${buttonStyle(cta.buttons[0], displayMode)}">
                ${cta.buttons[0].text}
              </a>
            ` : ''}
          </div>
        ` : `
          ${cta.image?.url ? `<div class="image-wrapper">${imageHtml}</div>` : ''}
          <div class="text-content">
            ${cta.headline?.text ? `<h1 class="headline">${cta.headline.text}</h1>` : ''}
            ${cta.tagline?.text ? `<p class="tagline">${cta.tagline.text}</p>` : ''}
            ${cta.bodyText?.text ? `<p class="body-text">${cta.bodyText.text}</p>` : ''}
            ${listItemsHtml}
            ${pricingHtml}
            ${eventHtml}
          </div>
          <div class="buttons-wrapper">
            ${cta.buttons?.[0]?.text ? `
              <a href="${cta.buttons[0].url || '#'}" target="_blank" class="cta-btn" style="${buttonStyle(cta.buttons[0], displayMode)}">
                ${cta.buttons[0].text}
              </a>
            ` : ''}
            ${cta.secondaryButton?.enabled && cta.secondaryButton?.text ? `
              <a href="${cta.secondaryButton.url || '#'}" target="_blank" class="cta-btn" style="${secondaryButtonStyle(cta.secondaryButton, displayMode)}">
                ${cta.secondaryButton.text}
              </a>
            ` : ''}
          </div>
        `}
      </div>
    </body>
    </html>
  `;
}

// Preview component for the modal
export default function CTARenderer({ cta, displayMode, className = '' }) {
  const html = generateCTAHtml(cta, displayMode);

  return (
    <iframe
      srcDoc={html}
      className={`border-0 ${className}`}
      title="CTA Preview"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
    />
  );
}
